import { useAuth } from "@clerk/nextjs";
import clsx from "clsx";
import { ChevronRightIcon, CircleIcon, XIcon } from "lucide-react";
import { FaCheckCircle } from "react-icons/fa";

import useOnboardingChecklist from "~/hooks/useOnboardingChecklist";

interface OnboardingChecklistProps {
	variant?: "inline" | "modal";
}

const OnboardingChecklist = ({
	variant = "inline",
}: OnboardingChecklistProps) => {
	const {
		checklist,
		isLoadingChecklist,
		isDismissed,
		numberOfRemainingSteps,
		dismissChecklist,
	} = useOnboardingChecklist();

	const { userId } = useAuth();

	if (
		!userId ||
		isLoadingChecklist ||
		isDismissed ||
		!checklist ||
		checklist.every((item) => item.isCompleted)
	) {
		return null;
	}

	const isModal = variant === "modal";

	return (
		<div
			className={clsx(
				"relative flex flex-col gap-3",
				!isModal &&
					"rounded-2xl border bg-white px-5 pt-4 pb-5 shadow shadow-black/10 dark:bg-slate-800 dark:shadow-slate-950",
			)}
		>
			{!isModal && (
				<button
					type="button"
					aria-label="Dismiss onboarding checklist"
					onClick={dismissChecklist}
					className="absolute right-4.5 rounded-md p-1 hover:bg-black/10 dark:hover:bg-white/10"
				>
					<XIcon className="size-4" />
				</button>
			)}

			<div className="flex flex-col gap-0.5">
				{!isModal && (
					<h2 className="font-semibold text-black text-lg dark:text-white">
						First Steps to Get Started
					</h2>
				)}

				<p className="text-sky-700 text-sm dark:text-sky-200">
					{numberOfRemainingSteps} Step
					{(numberOfRemainingSteps || 0) > 1 && "s"} Remaining
				</p>
			</div>

			<div className="flex flex-col gap-1">
				{checklist.map((item) => (
					<button
						key={item.title}
						type="button"
						onClick={item.handleClick}
						disabled={item.isCompleted}
						className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 py-1.5 text-left font-medium transition-colors hover:bg-slate-100 disabled:pointer-events-none disabled:text-slate-400 dark:disabled:text-slate-500 dark:hover:bg-slate-700"
					>
						{item.isCompleted ? (
							<FaCheckCircle
								size={18}
								className="shrink-0 text-emerald-500 dark:text-emerald-400"
							/>
						) : (
							<CircleIcon
								size={18}
								className="shrink-0 text-slate-400 dark:text-slate-500"
							/>
						)}

						<span>{item.title}</span>

						{!item.isCompleted && (
							<ChevronRightIcon
								size={18}
								className="ml-auto shrink-0 text-slate-400 transition-transform group-hover:translate-x-0.5"
							/>
						)}
					</button>
				))}
			</div>
		</div>
	);
};

export default OnboardingChecklist;
